import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { ArrowUp, ArrowDown, X, Plus } from 'lucide-react';
import type { TiebreakCriterion } from '@swiss-magic/shared-types';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';

function slugify(s: string): string {
  return s.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '');
}

function dashSlug(s: string): string {
  return s.toLowerCase().replace(/[^a-z0-9]+/g, '-');
}

function criterionLabel(criterion: TiebreakCriterion, t: TFunction): string {
  // i18next returns the key itself on a miss. Slugified keys live under
  // tournament_form.tiebreak_criteria.*; raw criterion strings are the
  // fallback so unknown values don't render as a key path.
  const key = `tournament_form.tiebreak_criteria.${slugify(criterion)}`;
  const translated = t(key);
  return translated === key ? criterion : translated;
}

type Props = {
  tiebreakCriteria: TiebreakCriterion[];
  availableTiebreaks: TiebreakCriterion[];
  onAdd: (criterion: TiebreakCriterion) => void;
  onRemove: (criterion: TiebreakCriterion) => void;
  onMove: (index: number, direction: -1 | 1) => void;
};

export function TiebreakCriteriaEditor({
  tiebreakCriteria,
  availableTiebreaks,
  onAdd,
  onRemove,
  onMove,
}: Props) {
  const { t } = useTranslation('director');
  const addable = availableTiebreaks.filter((c) => !tiebreakCriteria.includes(c));

  return (
    <div className="space-y-2">
      <Label>{t('tournament_form.tiebreak_label')}</Label>
      <div data-testid="tiebreak-criteria-list" className="space-y-1">
        {tiebreakCriteria.map((criterion, index) => (
          <div
            key={criterion}
            className="flex items-center gap-1 rounded border px-2 py-1"
            data-testid={`tiebreak-item-${dashSlug(criterion)}`}
          >
            <span className="w-5 text-sm text-muted-foreground">{index + 1}.</span>
            <span className="flex-1 text-sm">{criterionLabel(criterion, t)}</span>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-6 w-6 p-0"
              disabled={index === 0}
              onClick={() => onMove(index, -1)}
              data-testid={`tiebreak-up-${dashSlug(criterion)}`}
            >
              <ArrowUp className="h-3 w-3" />
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-6 w-6 p-0"
              disabled={index === tiebreakCriteria.length - 1}
              onClick={() => onMove(index, 1)}
              data-testid={`tiebreak-down-${dashSlug(criterion)}`}
            >
              <ArrowDown className="h-3 w-3" />
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-6 w-6 p-0"
              onClick={() => onRemove(criterion)}
              data-testid={`tiebreak-remove-${dashSlug(criterion)}`}
            >
              <X className="h-3 w-3" />
            </Button>
          </div>
        ))}
      </div>
      {addable.length > 0 && (
        <Select value="" onValueChange={(v) => onAdd(v as TiebreakCriterion)}>
          <SelectTrigger data-testid="tiebreak-add-select">
            <SelectValue placeholder={t('tournament_form.tiebreak_add_placeholder')} />
          </SelectTrigger>
          <SelectContent>
            {addable.map((criterion) => (
              <SelectItem key={criterion} value={criterion}>
                <Plus className="mr-1 inline h-3 w-3" />
                {criterionLabel(criterion, t)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}
    </div>
  );
}
